<template>
    <div class="lesson-chat-history-viewer">
        <b-modal
            :id="modalId"
            :title="modalTitle"
            size="lg"
            scrollable
            hide-footer
            @hidden="onModalHidden"
        >
            <div v-if="loading" class="text-center py-4">
                <b-spinner variant="primary" label="Loading"></b-spinner>
            </div>
            <div v-else-if="loadError" class="alert alert-warning mb-0">
                {{ loadError }}
            </div>
            <div v-else-if="messages.length === 0" class="text-center text-muted py-4 small">
                No lesson chat messages for this lesson.
            </div>
            <div v-else class="lesson-chat-history-list">
                <div
                    v-for="msg in messages"
                    :key="'lch-' + msg.id"
                    class="lesson-chat-history-item"
                    :class="{
                        'lesson-chat-history-item--mine': isMine(msg),
                        'lesson-chat-history-item--inserted': msg.is_inserted,
                    }"
                >
                    <div class="lesson-chat-history-meta small text-muted">
                        <strong>{{ senderLabel(msg) }}</strong>
                        <span v-if="formatTime(msg)"> · {{ formatTime(msg) }}</span>
                        <span v-if="msg.is_inserted" class="lesson-chat-inserted-badge">Inserted message</span>
                    </div>
                    <div class="lesson-chat-history-body chat-message-body" v-html="formatMessage(msg.message)"></div>
                    <div v-if="groupedReactions(msg).length" class="lesson-chat-history-reactions">
                        <span
                            v-for="r in groupedReactions(msg)"
                            :key="r.emoji"
                            class="lesson-chat-history-reaction-pill"
                            :title="r.title"
                        >{{ r.emoji }} <span class="lesson-chat-history-reaction-count">{{ r.count }}</span></span>
                    </div>
                </div>
            </div>

            <div class="text-right mt-3">
                <b-button size="sm" variant="secondary" @click="close">Close</b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import * as Moment from 'moment';

export default {
    name: 'LessonChatHistoryViewerComponent',
    props: {
        api_token: {
            type: String,
            required: true,
        },
        modal_id: {
            type: String,
            default: 'lesson-chat-history-modal',
        },
    },
    data() {
        return {
            scheduleId: null,
            modalTitle: 'Lesson chat',
            loading: false,
            loadError: null,
            messages: [],
            currentUserId: null,
        };
    },
    computed: {
        modalId() {
            return this.modal_id;
        },
    },
    mounted() {
        if (typeof window !== 'undefined' && window.__lessonChatViewerUserId) {
            this.currentUserId = String(window.__lessonChatViewerUserId);
        }
        document.addEventListener('click', this.onDocumentClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick);
    },
    methods: {
        onDocumentClick(event) {
            const trigger = event.target.closest('.js-view-lesson-chat');
            if (!trigger) {
                return;
            }
            event.preventDefault();
            const scheduleId = trigger.getAttribute('data-schedule-id') || trigger.dataset.scheduleId;
            const label = trigger.getAttribute('data-lesson-label') || trigger.dataset.lessonLabel || '';
            if (scheduleId) {
                this.open(scheduleId, label);
            }
        },
        open(scheduleId, title) {
            this.scheduleId = String(scheduleId);
            this.modalTitle = title ? `Lesson chat — ${title}` : 'Lesson chat';
            this.loadError = null;
            this.messages = [];
            this.$bvModal.show(this.modalId);
            this.fetchHistory();
        },
        close() {
            this.$bvModal.hide(this.modalId);
        },
        onModalHidden() {
            this.loading = false;
            this.loadError = null;
            this.messages = [];
            this.scheduleId = null;
        },
        isMine(msg) {
            if (!this.currentUserId || !msg || msg.sender_id == null) {
                return false;
            }
            return String(msg.sender_id) === this.currentUserId;
        },
        senderLabel(msg) {
            if (msg.nickname) {
                return msg.nickname;
            }
            if (msg.firstname) {
                return msg.firstname;
            }
            if (msg.message_type === 'TUTOR') {
                return 'Tutor';
            }
            return 'Student';
        },
        formatTime(msg) {
            const raw = msg.sent_at || msg.created_at;
            if (!raw) {
                return '';
            }
            const m = Moment(raw);
            return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : String(raw);
        },
        userLabel(userId) {
            if (this.currentUserId && String(userId) === this.currentUserId) {
                return 'You';
            }
            const match = this.messages.find((m) => String(m.sender_id) === String(userId));
            return match ? this.senderLabel(match) : 'Someone';
        },
        groupedReactions(msg) {
            if (!msg.reactions || !msg.reactions.length) {
                return [];
            }
            const groups = {};
            const order = [];
            msg.reactions.forEach((r) => {
                if (!groups[r.emoji]) {
                    groups[r.emoji] = { emoji: r.emoji, count: 0, users: [] };
                    order.push(r.emoji);
                }
                groups[r.emoji].count += 1;
                groups[r.emoji].users.push(this.userLabel(r.user_id));
            });
            return order.map((emoji) => {
                const group = groups[emoji];
                return { ...group, title: group.users.join(', ') };
            });
        },
        formatMessage(html) {
            if (html == null || html === '') {
                return '';
            }
            return String(html);
        },
        fetchHistory() {
            if (!this.scheduleId || !this.api_token) {
                this.loadError = 'Missing lesson or login token.';
                return;
            }
            this.loading = true;
            axios.post('/api/getLessonChathistory?api_token=' + encodeURIComponent(this.api_token), {
                method: 'POST',
                channelid: this.scheduleId,
            }).then((response) => {
                this.loading = false;
                if (response.data && response.data.success === true) {
                    const list = response.data.allChatHistoryItems || [];
                    this.messages = Array.isArray(list) ? list : [];
                    return;
                }
                if (response.data && response.data.success === false) {
                    this.messages = [];
                    return;
                }
                this.loadError = 'Could not load lesson chat.';
            }).catch((error) => {
                this.loading = false;
                const status = error.response && error.response.status;
                if (status === 403) {
                    this.loadError = 'You do not have permission to view this lesson chat.';
                } else {
                    this.loadError = 'Could not load lesson chat.';
                }
                console.warn('[lesson-chat-history]', error);
            });
        },
    },
};
</script>

<style scoped>
.lesson-chat-history-list {
    max-height: 60vh;
    overflow-y: auto;
    padding: 0.25rem;
}

.lesson-chat-history-item {
    margin-bottom: 0.75rem;
    padding: 0.5rem 0.65rem;
    border-radius: 8px;
    background: #f1f5f9;
}

.lesson-chat-history-item--mine {
    background: #e0f2fe;
}

.lesson-chat-history-item--inserted {
    border-left: 3px solid #f59e0b;
}

.lesson-chat-inserted-badge {
    display: inline-block;
    margin-left: 0.35rem;
    padding: 0 0.4rem;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #92400e;
    background: #fef3c7;
    border-radius: 4px;
    vertical-align: middle;
}

.lesson-chat-history-body >>> img {
    max-width: 100%;
    height: auto;
}

.lesson-chat-history-reactions {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 0.35rem;
}

.lesson-chat-history-reaction-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #fff;
    border: 1px solid #d6dde5;
    border-radius: 12px;
    padding: 1px 7px;
    font-size: 12px;
    line-height: 1.5;
    color: #555;
}

.lesson-chat-history-reaction-count {
    font-size: 11px;
    color: inherit;
}
</style>
